import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { LoginDialog } from '@/components/auth/login-dialog'
import { useAuth, useRequestSession } from '@/lib/api/auth'
import { gqlRequest, graphqlErrorMessage } from '@/lib/api/client'
import { OrderDisplayDataQuery } from '@/lib/api/operations'
import { board, type DisplayOrder, newlyReady, ORDER_DISPLAY_FONTS, type OrderDisplayConfig } from '@/lib/order-display'
import { playNewOrderChime, unlockSound } from '@/lib/sound'
import { useConfiguration } from '@/lib/useConfiguration'

/**
 * One column of numbers. Nothing here is interactive: it hangs on a wall and is read
 * from across a room, so everything is as large as it can be and there is no chrome.
 */
function Column({ heading, orders, config, tone, highlighted }: { heading: string; orders: DisplayOrder[]; config: OrderDisplayConfig; tone: string; highlighted: Set<string> }) {
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col">
      <h2
        className="border-b-4 px-6 py-4 font-bold tracking-wide uppercase"
        style={{
          borderColor: tone,
          color: tone,
          fontSize: `${(config.headingScale / 100) * 2.5}rem`,
        }}
      >
        {heading}
      </h2>
      <div className="grid min-h-0 flex-1 auto-rows-min gap-4 overflow-y-auto p-6" style={{ gridTemplateColumns: `repeat(${config.columnsPerSide}, minmax(0, 1fr))` }}>
        {orders.map((order) => (
          <div
            key={order.id}
            className="flex flex-col items-center justify-center rounded-2xl px-4 py-6 tabular-nums"
            style={{
              backgroundColor: highlighted.has(order.id) ? tone : `${tone}22`,
              color: highlighted.has(order.id) ? config.backgroundColor : config.textColor,
              fontSize: `${(config.numberScale / 100) * 4}rem`,
              lineHeight: 1,
              fontWeight: 800,
            }}
          >
            <span className="break-all">{order.humanOrderCode ?? '—'}</span>
            {config.showChannelBadges && order.orderType && <span className="mt-2 text-[0.22em] font-semibold tracking-widest uppercase opacity-70">{order.orderType}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}

export function OrderDisplay({ restaurantId }: { restaurantId: string }) {
  const { session, ready } = useAuth()
  const requestSession = useRequestSession()

  const configuration = useConfiguration(requestSession, restaurantId)
  const query = useQuery({
    queryKey: ['orderDisplay', requestSession.mode, restaurantId],
    enabled: Boolean(session),
    refetchInterval: configuration.config.orderDisplay.pollIntervalSeconds * 1000,
    queryFn: () => gqlRequest(requestSession, OrderDisplayDataQuery, { restaurantId }),
  })

  const config = configuration.config.orderDisplay
  const orders = useMemo(() => board(query.data?.restaurant?.kitchenDisplay ? [query.data.restaurant.kitchenDisplay] : []), [query.data])

  /**
   * A number that has just been called keeps a highlight for a while, because a guest
   * who looks up a moment later should still see that something changed.
   */
  const announced = useRef<Set<string> | null>(null)
  const highlighted = useRef<Map<string, number>>(new Map())
  const fresh = newlyReady(announced.current, orders.ready)
  announced.current = new Set(orders.ready.map((order) => order.id))
  const now = Date.now()
  for (const id of fresh) highlighted.current.set(id, now)
  for (const [id, at] of highlighted.current) if (now - at > config.highlightNewlyReadySeconds * 1000) highlighted.current.delete(id)

  useEffect(() => {
    if (fresh.length && config.chimeOnReady) playNewOrderChime(config.chimeVolume)
  }, [fresh.length, config.chimeOnReady, config.chimeVolume])

  useEffect(() => {
    const wake = () => unlockSound()
    window.addEventListener('pointerdown', wake, { once: true })
    return () => window.removeEventListener('pointerdown', wake)
  }, [])

  return (
    <div
      className="flex flex-1 min-h-0 flex-col"
      style={{
        backgroundColor: config.backgroundColor,
        color: config.textColor,
        fontFamily: ORDER_DISPLAY_FONTS[config.fontFamily] ?? ORDER_DISPLAY_FONTS.system,
      }}
    >
      <LoginDialog open={ready && !session} />

      {config.showLogo && config.logoUrl && (
        <header className="flex shrink-0 justify-center py-4">
          <img src={config.logoUrl} alt="" className="h-16 w-16 rounded-xl object-contain" />
        </header>
      )}

      {!session ? (
        <div className="flex-1" />
      ) : query.data?.restaurant?.galleyEnabled !== true && !query.isPending ? (
        <p className="p-8">Beställningsskärmen är inte aktiverad för restaurangen.</p>
      ) : query.data?.restaurant?.kitchenDisplaysDisabled ? (
        <p className="p-8">Köksskärmar är avstängda för restaurangen.</p>
      ) : query.isPending ? (
        <div role="status" className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin opacity-50" />
        </div>
      ) : query.error ? (
        <p role="alert" className="flex flex-1 items-center justify-center p-10 text-2xl">
          {graphqlErrorMessage(query.error)}
        </p>
      ) : (
        <main className="flex min-h-0 flex-1 divide-x-4" style={{ borderColor: config.accentColor }}>
          <Column heading={config.headingPreparing} orders={orders.preparing} config={config} tone={config.accentColor} highlighted={new Set()} />
          <Column heading={config.headingReady} orders={orders.ready} config={config} tone={config.readyColor} highlighted={new Set(highlighted.current.keys())} />
        </main>
      )}
    </div>
  )
}
